import { z } from "zod";
import { BusinessGoalV1Schema, ExecutionModeV1Schema, RiskPreferenceV1Schema } from "./ai-policy";

export const AiManagedGroupLifecycleStatusSchema = z.enum(["active", "paused", "archived"]);
export type AiManagedGroupLifecycleStatus = z.infer<typeof AiManagedGroupLifecycleStatusSchema>;

export const AiManagedGroupEffectiveStatusSchema = z.enum([
  "active",
  "paused_by_group",
  "paused_by_workspace",
  "archived",
]);
export type AiManagedGroupEffectiveStatus = z.infer<typeof AiManagedGroupEffectiveStatusSchema>;

export const AiManagedGroupPolicySchema = z
  .object({
    businessGoal: BusinessGoalV1Schema,
    targetAcos: z.number().finite().min(0.01).max(2),
    riskPreference: RiskPreferenceV1Schema,
    executionMode: ExecutionModeV1Schema,
  })
  .strict();
export type AiManagedGroupPolicy = z.infer<typeof AiManagedGroupPolicySchema>;

const campaignIdsSchema = z
  .array(z.string().uuid())
  .min(1)
  .max(100)
  .superRefine((ids, context) => {
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "campaignIds must not contain duplicates",
      });
    }
  });

const expectedVersionSchema = z.number().int().positive();

export const AiManagedGroupCreateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    campaignIds: campaignIdsSchema,
    policy: AiManagedGroupPolicySchema,
    lifecycleStatus: z.enum(["active", "paused"]),
  })
  .strict();
export type AiManagedGroupCreateInput = z.infer<typeof AiManagedGroupCreateInputSchema>;

export const AiManagedGroupUpdateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    policy: AiManagedGroupPolicySchema.optional(),
    expectedVersion: expectedVersionSchema,
  })
  .strict()
  .refine((value) => value.name !== undefined || value.policy !== undefined, {
    message: "at least one AI managed group field is required",
  });
export type AiManagedGroupUpdateInput = z.infer<typeof AiManagedGroupUpdateInputSchema>;

export const AiManagedGroupLifecycleInputSchema = z
  .object({
    action: z.enum(["activate", "pause", "archive"]),
    expectedVersion: expectedVersionSchema,
  })
  .strict();
export type AiManagedGroupLifecycleInput = z.infer<typeof AiManagedGroupLifecycleInputSchema>;

export const AiManagedGroupCampaignsInputSchema = z
  .object({
    campaignIds: campaignIdsSchema,
    expectedVersion: expectedVersionSchema,
  })
  .strict();
export type AiManagedGroupCampaignsInput = z.infer<typeof AiManagedGroupCampaignsInputSchema>;

export const AiManagedGroupMoveCampaignsInputSchema = z
  .object({
    campaignIds: campaignIdsSchema,
    targetGroupId: z.string().uuid(),
    sourceExpectedVersion: expectedVersionSchema,
    targetExpectedVersion: expectedVersionSchema,
  })
  .strict();
export type AiManagedGroupMoveCampaignsInput = z.infer<
  typeof AiManagedGroupMoveCampaignsInputSchema
>;

export const AiManagedGroupMemberAiInputSchema = z
  .object({
    aiManaged: z.boolean(),
    expectedVersion: expectedVersionSchema,
  })
  .strict();
export type AiManagedGroupMemberAiInput = z.infer<typeof AiManagedGroupMemberAiInputSchema>;

export const CampaignManagedGroupSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    lifecycleStatus: AiManagedGroupLifecycleStatusSchema,
    effectiveStatus: AiManagedGroupEffectiveStatusSchema,
  })
  .strict();
export type CampaignManagedGroup = z.infer<typeof CampaignManagedGroupSchema>;

export const AiManagedGroupMemberSchema = z
  .object({
    assignmentId: z.string().uuid(),
    campaignId: z.string().uuid(),
    name: z.string(),
    status: z.enum(["enabled", "paused", "archived"]),
    targetingType: z.enum(["manual", "auto"]).nullable(),
    aiManaged: z.boolean(),
    assignedAt: z.string().datetime(),
  })
  .strict();
export type AiManagedGroupMember = z.infer<typeof AiManagedGroupMemberSchema>;

export const AiManagedGroupSchema = z
  .object({
    id: z.string().uuid(),
    shopId: z.string().uuid(),
    name: z.string(),
    lifecycleStatus: AiManagedGroupLifecycleStatusSchema,
    effectiveStatus: AiManagedGroupEffectiveStatusSchema,
    workspaceAiEnabled: z.boolean(),
    policy: AiManagedGroupPolicySchema,
    version: z.number().int().positive(),
    memberCount: z.number().int().nonnegative(),
    enabledMemberCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type AiManagedGroup = z.infer<typeof AiManagedGroupSchema>;

export const AiManagedGroupDetailSchema = AiManagedGroupSchema.extend({
  members: z.array(AiManagedGroupMemberSchema),
}).strict();
export type AiManagedGroupDetail = z.infer<typeof AiManagedGroupDetailSchema>;

export const AiManagedGroupListResponseSchema = z
  .object({ success: z.literal(true), data: z.array(AiManagedGroupSchema) })
  .strict();

export const AiManagedGroupDetailResponseSchema = z
  .object({ success: z.literal(true), data: AiManagedGroupDetailSchema })
  .strict();

export const AiManagedGroupEligibleCampaignSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    status: z.enum(["enabled", "paused", "archived"]),
    targetingType: z.enum(["manual", "auto"]).nullable(),
    aiManaged: z.boolean(),
    portfolio: z
      .object({
        id: z.string().uuid(),
        name: z.string(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type AiManagedGroupEligibleCampaign = z.infer<typeof AiManagedGroupEligibleCampaignSchema>;

export const AiManagedGroupEligibleCampaignsQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(50).default(10),
    search: z.string().trim().max(100).optional(),
    status: z.enum(["enabled", "paused", "archived"]).optional(),
    portfolioId: z.union([z.string().uuid(), z.literal("unassigned")]).optional(),
    aiStatus: z.enum(["on", "off"]).optional(),
    sortBy: z.enum(["aiStatus", "delivery"]).optional(),
    sortDirection: z.enum(["asc", "desc"]).optional(),
    excludeCampaignIds: z
      .preprocess(
        (value) => (typeof value === "string" ? value.split(",").filter(Boolean) : value),
        z.array(z.string().uuid()).max(100),
      )
      .optional(),
  })
  .strict();
export type AiManagedGroupEligibleCampaignsQuery = z.infer<
  typeof AiManagedGroupEligibleCampaignsQuerySchema
>;

export const AiManagedGroupEligibleCampaignsPaginationSchema = z
  .object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive().max(50),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
  })
  .strict();

export const AiManagedGroupEligibleCampaignsResponseSchema = z
  .object({
    success: z.literal(true),
    data: z.array(AiManagedGroupEligibleCampaignSchema),
    pagination: AiManagedGroupEligibleCampaignsPaginationSchema,
  })
  .strict();
export type AiManagedGroupEligibleCampaignsResponse = z.infer<
  typeof AiManagedGroupEligibleCampaignsResponseSchema
>;

export const AiManagedGroupPortfolioOptionSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
  })
  .strict();
export type AiManagedGroupPortfolioOption = z.infer<typeof AiManagedGroupPortfolioOptionSchema>;

export const AiManagedGroupPortfolioOptionsResponseSchema = z
  .object({
    success: z.literal(true),
    data: z.array(AiManagedGroupPortfolioOptionSchema),
  })
  .strict();
